import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errorMessage } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { Banner } from '../components/Banner';
import {
  clockTime,
  dayKey,
  dayDate,
  dayLabel,
  daysFrom,
  SLOTS,
  SLOT_NAMES,
  slotHasPassed,
  startOfWeek,
  startsAtFor,
  timeLeft,
  type SessionView,
  type Slot,
} from '../lib/sessions';

const STATE_TONE: Record<SessionView['state'], string> = {
  PLANNED: '',
  RUNNING: 'tag--on',
  CLOSED: 'tag--off',
};

/**
 * The test-day timetable.
 *
 * A week at a time, three slots a day, because that is the shape of the thing
 * being planned: a centre says "Saturday morning" and means one room, one
 * start, one set of papers. An empty slot is a place to put one rather than
 * nothing at all — the whole grid is the offer.
 */
export function SchedulePage() {
  const navigate = useNavigate();
  const [monday, setMonday] = useState(() => startOfWeek(new Date()));
  const [sessions, setSessions] = useState<SessionView[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const week = useMemo(() => daysFrom(monday, 7), [monday]);
  const from = dayKey(week[0]!);
  const to = dayKey(week[6]!);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get<{ sessions: SessionView[] }>('/sessions', {
        params: { from, to },
      });
      setSessions(data.sessions);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the timetable'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  /** Every slot of the week, by its own key, so a cell is one lookup. */
  const byCell = useMemo(() => {
    const map = new Map<string, SessionView>();
    for (const session of sessions) map.set(`${session.day}:${session.slot}`, session);
    return map;
  }, [sessions]);

  /**
   * Putting a session in an empty slot.
   *
   * Created straight away at the slot's usual hour rather than behind a form:
   * everything about it — the time, the name, the papers, the room — is on the
   * screen it opens, and an empty session is deletable in one click.
   */
  async function schedule(day: string, slot: Slot) {
    const cell = `${day}:${slot}`;
    setBusy(cell);
    setError(null);
    try {
      const { data } = await api.post<{ session: SessionView }>('/sessions', {
        day,
        slot,
        startsAt: startsAtFor(day, slot).toISOString(),
      });
      navigate(`/schedule/${data.session.id}`);
    } catch (err) {
      setError(errorMessage(err, 'Could not schedule that session'));
      setBusy(null);
    }
  }

  const thisWeek = dayKey(startOfWeek(new Date())) === from;

  return (
    <>
      <PageHeader
        title="Test schedule"
        meta={
          <p className="muted">
            <span className="mono">{sessions.length}</span>{' '}
            {sessions.length === 1 ? 'session' : 'sessions'} this week
          </p>
        }
        actions={
          <>
            <button
              className="btn"
              onClick={() => setMonday(shiftWeek(monday, -1))}
              aria-label="The week before"
            >
              ‹ Earlier
            </button>
            <button className="btn" disabled={thisWeek} onClick={() => setMonday(startOfWeek(new Date()))}>
              This week
            </button>
            <button
              className="btn"
              onClick={() => setMonday(shiftWeek(monday, 1))}
              aria-label="The week after"
            >
              Later ›
            </button>
          </>
        }
      />

      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}

        <p className="field__hint" style={{ maxWidth: '64ch' }}>
          Three sittings a day, four and a half hours each. Everybody in a session starts together
          and it closes when the time is up, finished or not — so a paper still open at the end is
          marked as it stands.
        </p>

        {loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <div className="timetable">
            {week.map((date) => {
              const day = dayKey(date);
              const today = day === dayKey(new Date());
              return (
                <section key={day} className={`timetable__day ${today ? 'timetable__day--now' : ''}`}>
                  <h2 className="timetable__date">
                    {dayLabel(day)}
                    <span className="timetable__when">{dayDate(day)}</span>
                  </h2>
                  <div className="timetable__slots">
                    {SLOTS.map((slot) => {
                      const session = byCell.get(`${day}:${slot}`);
                      if (!session) {
                        /*
                         * A slot whose four and a half hours are already over
                         * cannot hold a session: one scheduled into it is born
                         * closed, and a closed session refuses papers,
                         * candidates and its own name. Offering the button was
                         * offering a dead card.
                         */
                        if (slotHasPassed(day, slot)) {
                          return (
                            <div key={slot} className="slot slot--gone">
                              <span className="slot__name">{SLOT_NAMES[slot]}</span>
                              <span className="muted">Passed</span>
                            </div>
                          );
                        }
                        return (
                          <button
                            key={slot}
                            className="slot slot--free"
                            aria-label={`Schedule the ${SLOT_NAMES[slot].toLowerCase()} session on ${dayLabel(day)}`}
                            disabled={busy === `${day}:${slot}`}
                            onClick={() => void schedule(day, slot)}
                          >
                            <span className="slot__name">{SLOT_NAMES[slot]}</span>
                            <span className="muted">
                              {busy === `${day}:${slot}` ? 'Scheduling…' : 'Schedule'}
                            </span>
                          </button>
                        );
                      }
                      return (
                        <button
                          key={slot}
                          className="slot slot--set"
                          aria-label={`Open the ${SLOT_NAMES[slot].toLowerCase()} session on ${dayLabel(day)}`}
                          onClick={() => navigate(`/schedule/${session.id}`)}
                        >
                          <span className="slot__head">
                            <span className="slot__name">{SLOT_NAMES[slot]}</span>
                            <span className={`tag ${STATE_TONE[session.state]}`}>
                              {session.state === 'RUNNING'
                                ? timeLeft(session.endsAt)
                                : session.state === 'CLOSED'
                                  ? 'Closed'
                                  : clockTime(session.startsAt)}
                            </span>
                          </span>
                          {session.name && <span className="slot__title">{session.name}</span>}
                          <span className="muted">
                            <span className="mono">{session.candidates.length}</span> sitting ·{' '}
                            <span className="mono">{session.tests}</span>{' '}
                            {session.tests === 1 ? 'test' : 'tests'}
                            {session.shuffle ? ' · shuffled' : ''}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

/** The Monday `weeks` weeks away, which is all the navigation this needs. */
function shiftWeek(monday: Date, weeks: number): Date {
  const out = new Date(monday);
  out.setDate(out.getDate() + weeks * 7);
  return out;
}
